/**
 * Shared parser + status taxonomy for the customer-ERP "progress" narrative
 * surfaced in the Job Status column / inline expansion / modal tab.
 *
 * Centralised here so the regex, the colour buckets, and the relative-time
 * helper can't drift between the three render sites — earlier copies in the
 * cell renderer, the inline panel, and the modal tab disagreed on detail.
 *
 * Inputs come from POChangeLog.new_value with field_changed='progress', e.g.
 * "Job 0000096492_1 Qty5 was ready for dispatch.".
 */

// Job IDs are 10-digit zero-padded ("0000096642") with an optional sub-job
// suffix ("_1") generated when an ERP re-run splits a partial qty. The suffix
// matters: those are different jobs, not duplicates.
export const JOB_PROGRESS_RE = /Job\s+(\d{4,}(?:_\d+)?)(?:\s+Qty(\d+))?\s+(.+?)\.?$/i;

export interface ParsedProgress {
  jobNumber: string | null;
  qty: string | null;
  statusText: string;       // verb phrase: "is in process" / "was ready for dispatch"
}

export function parseProgress(text: string | null | undefined): ParsedProgress {
  const fallback: ParsedProgress = { jobNumber: null, qty: null, statusText: text || '' };
  if (!text) return fallback;
  const m = text.match(JOB_PROGRESS_RE);
  if (!m) return fallback;
  return { jobNumber: m[1], qty: m[2] ?? null, statusText: m[3].trim() };
}

// Four semantic buckets — design call: dispatched is positive (terminal good)
// not "different category", so it folds into the green family with complete.
// Six colours of similar saturation didn't scan; four with clear meaning do.
export type StatusBucket = 'positive' | 'active' | 'attention' | 'neutral';

export function statusBucket(statusText: string): StatusBucket {
  const t = statusText.toLowerCase();
  if (t.includes('hold')) return 'attention';
  if (t.includes('complete') || t.includes('dispatch') || t.includes('despatch') || t.includes('shipped') || t.includes('packed')) return 'positive';
  if (t.includes('process') || t.includes('progress') || t.includes('inspect')) return 'active';
  return 'neutral';
}

const BUCKET_CLASSES: Record<StatusBucket, string> = {
  positive: 'bg-emerald-100 text-emerald-800',
  active: 'bg-sky-100 text-sky-800',
  attention: 'bg-amber-100 text-amber-800',
  neutral: 'bg-slate-100 text-slate-700',
};

export function statusBadgeClass(statusText: string): string {
  return BUCKET_CLASSES[statusBucket(statusText)];
}

/**
 * Short, human label suitable for a chip — squashes the verb phrase from the
 * ERP narrative ("is in process", "was ready for dispatch") into one or two
 * tokens that fit a 70-px pill without truncation.
 *
 * Specific patterns first; pre-job lifecycle events ("Order was received",
 * "Contract Review was completed") get their own labels because the user
 * needs to distinguish them — generic "Received" / "Complete" loses meaning.
 */
export function shortStatusLabel(statusText: string): string {
  const t = statusText.toLowerCase();
  if (t.includes('order') && t.includes('received')) return 'Order received';
  if (t.includes('contract') && (t.includes('complete') || t.includes('review'))) return 'Contract reviewed';
  if (t.includes('dispatch') || t.includes('despatch') || t.includes('shipped') || t.includes('packed')) return 'Dispatched';
  if (t.includes('hold')) return 'On hold';
  if (t.includes('inspect')) return 'Inspection';
  if (t.includes('process') || t.includes('progress')) return 'In process';
  if (t.includes('complete')) return 'Complete';
  if (t.includes('received') && !t.includes('not')) return 'Received';
  return statusText.charAt(0).toUpperCase() + statusText.slice(1);
}

/**
 * Lucide icon name for the lifecycle stage. Returned as a string so the
 * parser module stays free of React/lucide imports — render sites do
 * `const Icon = ICON_MAP[name]`.
 */
export type StatusIconName = 'inbox' | 'fileCheck' | 'cog' | 'pauseCircle' | 'packageCheck' | 'circle';

export function statusIconName(statusText: string): StatusIconName {
  const t = statusText.toLowerCase();
  if (t.includes('order') && t.includes('received')) return 'inbox';
  if (t.includes('contract') && (t.includes('complete') || t.includes('review'))) return 'fileCheck';
  if (t.includes('dispatch') || t.includes('despatch') || t.includes('shipped') || t.includes('packed') || t.includes('complete')) return 'packageCheck';
  if (t.includes('hold')) return 'pauseCircle';
  if (t.includes('process') || t.includes('progress') || t.includes('inspect')) return 'cog';
  return 'circle';
}

export function relativeAge(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const diffMs = Date.now() - new Date(dateString).getTime();
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

/**
 * Visual shortening for the 10-digit zero-padded job IDs Poeton emits —
 * "0000096492_1" → "…96492_1". Full value lives in the title attribute so
 * the display saves real estate without losing data.
 */
export function shortJobNumber(jobNumber: string | null | undefined): string {
  if (!jobNumber) return '';
  // Strip leading zeros up to a 4-digit core; preserve any "_N" suffix.
  return jobNumber.replace(/^0+(?=\d{4,})/, '');
}
